/**
 * usePageFilters — per-user dashboard filter persistence.
 *
 * Read once via RTK Query (shared cache), debounced save on change.
 * Used for FactoryPage / ProcurementPage / SaleControlPage filter chips.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  useGetUserPageFiltersQuery,
  usePutUserPageFiltersMutation,
  type PageFiltersValue,
} from '@/store/api/userPreferencesApi'

const SAVE_DEBOUNCE_MS = 400

export interface UsePageFiltersResult<T extends PageFiltersValue> {
  /** Filter state — undefined until backend resolves (caller treats as default). */
  filters: T | undefined
  /** True khi đã load xong từ backend (kể cả khi chưa có row). */
  ready: boolean
  /** Save filters — accepts value or functional updater. Debounced save sang backend. */
  setFilters: (next: T | ((prev: T | undefined) => T)) => void
}

export function usePageFilters<T extends PageFiltersValue>(pageKey: string): UsePageFiltersResult<T> {
  const { data: allFilters, isSuccess } = useGetUserPageFiltersQuery()
  const [putFilters] = usePutUserPageFiltersMutation()

  const remote = (allFilters?.[pageKey] as T | undefined) ?? undefined
  const [local, setLocal] = useState<T | undefined>(remote)

  const lastRemoteRef = useRef<T | undefined>(remote)
  useEffect(() => {
    if (remote !== lastRemoteRef.current) {
      lastRemoteRef.current = remote
      setLocal(remote)
    }
  }, [remote])

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingRef = useRef<T | null>(null)

  const flush = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    if (pendingRef.current == null) return
    const value = pendingRef.current
    pendingRef.current = null
    putFilters({ pageKey, filters: value }).catch(() => undefined)
  }, [pageKey, putFilters])

  useEffect(() => {
    return () => {
      flush()
    }
  }, [flush])

  const localRef = useRef<T | undefined>(local)
  localRef.current = local

  const setFilters = useCallback(
    (next: T | ((prev: T | undefined) => T)) => {
      const resolved = typeof next === 'function'
        ? (next as (prev: T | undefined) => T)(localRef.current)
        : next
      setLocal(resolved)
      pendingRef.current = resolved
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(flush, SAVE_DEBOUNCE_MS)
    },
    [flush],
  )

  return { filters: local, ready: isSuccess, setFilters }
}
